import { createContext, useContext, useEffect, useState } from "react";

const PigContext = createContext();

export function PigProvider({ children }) {

  // ===============================
  // PIG MAIN STATES
  // ===============================

  const [pigLoaded, setPigLoaded] = useState(false);

  const [pigType, setPigType] = useState("rect");

  const [pigVisible, setPigVisible] = useState(false);

  const [pigX, setPigX] = useState(120);

  const [pigY, setPigY] = useState(300);

  const [direction, setDirection] = useState("x+");

  const [pigPage, setPigPage] = useState("launcher");

  const [pigPaused, setPigPaused] = useState(false);

  // ===============================
  // LIVE VALVES
  // ===============================

  const isXV10902Open =
    localStorage.getItem("isXV10902Open") === "true";

  const isXV10903Open =
    localStorage.getItem("isXV10903Open") === "true";

  const isXV10905Open =
    localStorage.getItem("isXV10905Open") === "true";

  const isXV10906Open =
    localStorage.getItem("isXV10906Open") === "true";

  const isXV10907Open =
    localStorage.getItem("isXV10907Open") === "true";

  const isXV10908Open =
    localStorage.getItem("isXV10908Open") === "true";

  const isXV11001Open =
    localStorage.getItem("isXV11001Open") === "true";

  const isXV11002Open =
    localStorage.getItem("isXV11002Open") === "true";

  // ===============================
  // FLOW SPEED
  // ===============================

  const FIC_10906 =
    Number(localStorage.getItem("FIC_10906")) || 0;

  // ===============================
  // LOAD PIG
  // ===============================

  const loadPig = (type = "rect") => {

    setPigLoaded(true);

    setPigVisible(true);

    setPigType(type);

    setPigPage("launcher");

    setPigX(120);

    setPigY(300);

    setDirection("x+");
  };

  // ===============================
  // REMOVE PIG
  // ===============================

  const removePig = () => {

    setPigLoaded(false);

    setPigVisible(false);
  };

  // ===============================
  // MAIN MOVEMENT ENGINE
  // ===============================

  useEffect(() => {

    if (!pigLoaded || !pigVisible) return;

    const interval = setInterval(() => {

      // ===========================
      // SPEED
      // ===========================

      let speed = 1;

      if (FIC_10906 >= 520) {
        speed = 4;
      }

      else if (FIC_10906 >= 300) {
        speed = 2;
      }

      // ===========================
      // START CONDITION
      // ===========================

      if (
        !isXV10902Open &&
        !isXV10903Open &&
        pigPage === "launcher"
      ) {
        return;
      }

      // ===========================
      // PAUSE CONDITIONS
      // ===========================

      // Isolation check 1
      if (pigX >= 400 && !isXV10905Open) {
        setPigPaused(true);
        return;
      }

      // Isolation check 2
      if (
        pigX >= 900 &&
        pigY >= 500 &&
        !isXV10907Open
      ) {
        setPigPaused(true);
        return;
      }

      // Receiver isolation
      if (
        pigPage === "receiver" &&
        pigX <= 250 &&
        !isXV11001Open
      ) {
        setPigPaused(true);
        return;
      }

      setPigPaused(false);

      // ===========================
      // MAIN ROUTING
      // ===========================

      // ===========================
      // MAIN LAUNCHER LINE
      // ===========================

      if (pigPage === "launcher") {

        // ROUTE TO PROD-1
        if (
          pigX >= 600 &&
          isXV10908Open &&
          direction === "x+"
        ) {
          setDirection("y-");
        }

        // DOWNWARD PROD-1
        if (direction === "y-") {

          setPigY(prev => prev + speed);

          if (pigY >= 500) {
            setDirection("x-");
          }

          return;
        }

        // PROD-1 EXIT
        if (direction === "x-") {

          setPigX(prev => prev - speed);

          if (pigX <= 50) {
            setPigVisible(false);
          }

          return;
        }

        // ROUTE TO IMPORT
        if (
          pigX >= 1100 &&
          isXV10906Open &&
          direction === "x+"
        ) {
          setDirection("y+");
        }

        // IMPORT UPWARD
        if (direction === "y+") {

          setPigY(prev => prev - speed);

          if (pigY <= 120) {
            setDirection("x++");
          }

          return;
        }

        // IMPORT EXIT
        if (direction === "x++") {

          setPigX(prev => prev + speed);

          if (pigX >= 1450) {
            setPigVisible(false);
          }

          return;
        }

        // MAIN LINE CONTINUE
        setPigX(prev => prev + speed);

        // TRANSFER TO RECEIVER
        if (pigX >= 1700) {

          setPigPage("receiver");

          setPigX(1200);

          setPigY(300);

          setDirection("rx-");
        }
      }

      // ===========================
      // RECEIVER PAGE
      // ===========================

      if (pigPage === "receiver") {

        // RECEIVER BRANCH
        if (
          pigX <= 700 &&
          isXV11002Open &&
          direction === "rx-"
        ) {
          setDirection("ry-");
        }

        // DOWNWARD
        if (direction === "ry-") {

          setPigY(prev => prev + speed);

          if (pigY >= 520) {
            setDirection("rx+");
          }

          return;
        }

        // BRANCH EXIT
        if (direction === "rx+") {

          setPigX(prev => prev + speed);

          if (pigX >= 1450) {
            setPigVisible(false);
          }

          return;
        }

        // MAIN RECEIVER
        setPigX(prev => prev - speed);

      }

    }, 30);

    return () => clearInterval(interval);

  }, [

    pigLoaded,
    pigVisible,
    pigX,
    pigY,
    direction,
    pigPage,

    isXV10902Open,
    isXV10903Open,
    isXV10905Open,
    isXV10906Open,
    isXV10907Open,
    isXV10908Open,

    isXV11001Open,
    isXV11002Open,

    FIC_10906
  ]);

  return (

    <PigContext.Provider
      value={{

        pigLoaded,
        pigType,
        pigVisible,

        pigX,
        pigY,

        pigPage,

        pigPaused,

        loadPig,
        removePig
      }}
    >
      {children}
    </PigContext.Provider>
  );
}

export function usePig() {
  return useContext(PigContext);
}